import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

import { getPublicDoc } from '../../../crud/docs.crud';
import { useFormatMessage } from '../../../hooks';

interface IProps {
  isOpen: boolean;
  handleClose: () => void;
  contentType: string;
}
const useStyles = makeStyles((theme: any) => ({
  content: {
    width: '100%',
    height: 500,
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: 'auto',
      maxHeight: 'calc(100vh - 150px)',
      padding: '16px',
      paddingBottom: '8px',
      marginBottom: 0,
    },
    [theme.breakpoints.down(600)]: {
      width: '100%',
      height: 'auto',
      maxHeight: 'calc(100vh - 150px)',
      padding: '12px',
      paddingBottom: '8px',
      marginBottom: 0,
    },
    '& > *': {
      boxSizing: 'border-box',
      maxWidth: '100%',
    },
    '& table': {
      width: '100% !important',
      maxWidth: '100%',
      tableLayout: 'auto',
      fontSize: 'inherit',
      display: 'table',
      [theme.breakpoints.down('sm')]: {
        fontSize: '12px',
        display: 'block',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        '& td, & th': {
          padding: '8px 10px',
          minWidth: '80px',
        },
      },
    },
    '& img': {
      maxWidth: '100% !important',
      height: 'auto !important',
      display: 'block',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    '& section, & div': {
      maxWidth: '100%',
      boxSizing: 'border-box',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    '& .grid, & [class*="grid"], & [class*="cols-2"], & [class*="cols-3"], & [class*="cols-4"]':
      {
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: '1fr !important',
          '& > *': {
            width: '100%',
          },
        },
      },
    '& .card, & [class*="card"]': {
      [theme.breakpoints.down('sm')]: {
        padding: '12px !important',
        marginBottom: '12px',
      },
    },
    '& p, & span, & div, & li': {
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      [theme.breakpoints.down('sm')]: {
        fontSize: '14px',
        lineHeight: '1.5',
      },
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      wordWrap: 'break-word',
      [theme.breakpoints.down('sm')]: {
        fontSize: 'clamp(18px, 4vw, 28px) !important',
        lineHeight: '1.3',
      },
    },
    '& ul, & ol': {
      paddingLeft: '20px',
      [theme.breakpoints.down('sm')]: {
        paddingLeft: '16px',
      },
    },
    '& form, & input, & textarea, & select': {
      maxWidth: '100%',
      boxSizing: 'border-box',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    '& *': {
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    '& pre, & code': {
      maxWidth: '100%',
      overflowX: 'auto',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      [theme.breakpoints.down('sm')]: {
        fontSize: '12px',
      },
    },
  },
  container: {
    '& .MuiDialog-paper': {
      padding: 20,
      maxWidth: '90vw',
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100vw',
        width: '100vw',
        margin: 0,
        maxHeight: '100vh',
        height: '100vh',
        borderRadius: 0,
        padding: '0',
      },
      [theme.breakpoints.down(600)]: {
        padding: 0,
      },
    },
    [theme.breakpoints.down(560)]: {
      '& .MuiDialog-paper': {
        padding: 0,
      },
    },
  },
  htmlContent: {
    width: '100%',
    '& *': {
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
  },
}));
const TermDialog: React.FC<IProps & WrappedComponentProps> = ({
  isOpen,
  handleClose,
  contentType,
  intl,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const fm = useFormatMessage();
  const navigate = useNavigate();

  function getMessage(pathname: string) {
    switch (pathname) {
      case 'user_agreement':
        return 'SUBMENU.LEGAL_TERMS';
      case 'privacy_policy':
        return 'SUBMENU.LEGAL_PRIVACY';
      case 'marketplace_commission':
        return 'SUBMENU.MARKETPLACE.COMMISSION';
      case 'public_offer':
        return 'PUBLIC.OFFER';
      case 'shipping_policy':
        return 'SUBMENU.LEGAL_SHIPPING';
      case 'refund_policy':
        return 'SUBMENU.LEGAL_REFUND';
      case 'import_instruction':
        return 'SUBMENU.IMPORT.INSTRUCTION';
      case 'cookie_policy':
        return 'DOC.COOKIE_POLICY';
      default:
        return 'DOC.USER_AGREEMENT';
    }
  }

  useEffect(() => {
    setLoading(true);
    if (contentType !== '') {
      getPublicDoc(contentType)
        .then(res => {
          setContent(res.data.data.text);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          enqueueSnackbar(err.message, {
            variant: 'error',
          });
        });
    }
  }, [contentType]);

  const handleOpenDoc = () => {
    handleClose();
    navigate(`/document/read/${contentType}`);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      className={classes.container}
      fullWidth
      maxWidth='lg'
      PaperProps={{
        sx: {
          ...(isMobile && {
            maxWidth: '750px',
            width: '100%',
            margin: 0,
            maxHeight: '100vh',
            height: '100vh',
            borderRadius: 0,
          }),
        },
      }}
    >
      <DialogTitle id='alert-dialog-title'>
        <div
          style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
        >
          {' '}
          {intl.formatMessage({
            id: contentType ? getMessage(contentType) : 'DOC.USER_AGREEMENT',
          })}
          <CloseIcon style={{ cursor: 'pointer' }} onClick={handleClose} />
        </div>
      </DialogTitle>
      <DialogContent
        className={classes.content}
        style={{
          ...(!isMobile && { flex: 1 }),
          position: 'relative',
          ...(isMobile && {
            paddingBottom: 0,
            marginBottom: 0,
            flex: 'none',
          }),
        }}
      >
        {loading ? (
          <div style={loaderWrapStyle}>
            <CircularProgress size={20} />
          </div>
        ) : (
          <div
            className={classes.htmlContent}
            dangerouslySetInnerHTML={{ __html: content }}
            style={{ color: 'black' }}
          />
        )}
      </DialogContent>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          padding: isMobile ? '12px 16px' : '16px',
          paddingTop: isMobile ? '12px' : '16px',
        }}
      >
        <Button variant='outlined' onClick={handleClose}>
          {fm('COMMON.BUTTON.CLOSE')}
        </Button>
        <Button variant='outlined' onClick={handleOpenDoc}>
          {fm('BUTTON.OPEN')}
        </Button>
      </div>
    </Dialog>
  );
};

const loaderWrapStyle = {
  height: '100%',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

export default injectIntl(TermDialog);
